import { useState, type FormEvent } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../../utils/cn';
import styles from './Pagination.module.css';

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginationProps {
  pagination: PaginationMeta;
  onPageChange: (page: number) => void;
  className?: string;
}

function buildPageList(current: number, total: number, windowSize = 5): (number | 'ellipsis')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const half = Math.floor(windowSize / 2);
  let start = Math.max(2, current - half);
  let end = Math.min(total - 1, current + half);
  if (current - half < 2) end = Math.min(total - 1, end + (2 - (current - half)));
  if (current + half > total - 1) start = Math.max(2, start - (current + half - (total - 1)));

  const pages: (number | 'ellipsis')[] = [1];
  if (start > 2) pages.push('ellipsis');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < total - 1) pages.push('ellipsis');
  pages.push(total);
  return pages;
}

export function Pagination({ pagination, onPageChange, className }: PaginationProps) {
  const { page, limit, total, totalPages } = pagination;
  const [jumpValue, setJumpValue] = useState('');

  const safeTotalPages = Math.max(1, totalPages);
  const safePage = Math.min(Math.max(1, page), safeTotalPages);
  const firstRow = total === 0 ? 0 : (safePage - 1) * limit + 1;
  const lastRow = Math.min(safePage * limit, total);

  const list = buildPageList(safePage, safeTotalPages);

  function go(target: number) {
    if (target < 1 || target > safeTotalPages || target === safePage) return;
    onPageChange(target);
  }

  function onJumpSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const n = Number(jumpValue);
    if (Number.isInteger(n)) go(n);
    setJumpValue('');
  }

  return (
    <nav className={cn(styles.bar, className)} aria-label="Pagination">
      <div className={styles.summary} aria-live="polite">
        {total === 0 ? 'No results' : `${firstRow}–${lastRow} of ${total}`}
      </div>
      <div className={styles.controls}>
        <button
          type="button"
          className={styles.btn}
          onClick={() => go(safePage - 1)}
          disabled={safePage <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft size={14} aria-hidden />
        </button>
        {list.map((p, idx) =>
          p === 'ellipsis' ? (
            <span key={`e-${idx}`} className={styles.ellipsis} aria-hidden>
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={cn(styles.btn, p === safePage && styles.active)}
              aria-current={p === safePage ? 'page' : undefined}
              aria-label={`Go to page ${p}`}
              onClick={() => go(p)}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          className={styles.btn}
          onClick={() => go(safePage + 1)}
          disabled={safePage >= safeTotalPages}
          aria-label="Next page"
        >
          <ChevronRight size={14} aria-hidden />
        </button>
        {safeTotalPages > 7 ? (
          <form className={styles.jumpRow} onSubmit={onJumpSubmit}>
            <span>Go to</span>
            <input
              type="number"
              min={1}
              max={safeTotalPages}
              value={jumpValue}
              onChange={(e) => setJumpValue(e.target.value)}
              aria-label="Jump to page"
              className={styles.jumpInput}
            />
          </form>
        ) : null}
      </div>
    </nav>
  );
}
